import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Edit, Trash2, Monitor, Building2, Users, Package, ExternalLink, LayoutGrid } from "lucide-react";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { posAPI, tablesAPI, getApiErrorPayload } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Loading } from "@/components/ui/loader";
import { Pos, PosStatus } from "@/types";
import { getBestBadgeVariant, formatDate } from "@/lib/utils";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { StorefrontSettingsEditor } from "@/features/pos/components/StorefrontSettings";
import { StorefrontBannersSettings } from "@/features/pos/components/StorefrontBannersSettings";
import { isStorefrontBanner, type StorefrontBanner } from "@/features/pos/storefront-banners";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface PosTable {
	id: number;
	number: string;
	label: string | null;
	floor: string | null;
	zone: string | null;
	seats: number | null;
	isActive: boolean;
}

const PosViewPage: React.FC = () => {
	const { t } = useTranslation();
	const currency = useCurrency();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const [loading, setLoading] = useState(true);
	const [pos, setPos] = useState<Pos | null>(null);
	const [tables, setTables] = useState<PosTable[]>([]);
	const [storefrontBanners, setStorefrontBanners] = useState<StorefrontBanner[]>([]);

	useEffect(() => {
		if (id) {
			fetchPos();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	useEffect(() => {
		const branchId = pos?.branchId;
		if (!pos?.offersTableService || branchId == null) {
			setTables([]);
			return;
		}
		tablesAPI
			.getAll(branchId)
			.then((data: PosTable[]) => setTables(Array.isArray(data) ? data : []))
			.catch(() => setTables([]));
	}, [pos?.offersTableService, pos?.branchId]);

	const fetchPos = async () => {
		try {
			setLoading(true);
			const data = await posAPI.getById(id!);
			setPos(data);
			if (data.isMenuWeb) {
				try {
					const config = await posAPI.getStorefrontBanners(id!);
					setStorefrontBanners(
						Array.isArray(config?.banners) ? config.banners.filter(isStorefrontBanner) : [],
					);
				} catch {
					setStorefrontBanners([]);
				}
			} else {
				setStorefrontBanners([]);
			}
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("pos.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/pos/list");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async () => {
		if (await confirmAction(t("pos.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await posAPI.delete(id!);
				toast({
					title: t("common.success"),
					description: t("pos.messages.deleted"),
				});
				navigate("/pos/list");
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("common.error"),
					description: errorMessage || t("common.errors.unexpected"),
					variant: "destructive",
				});
			}
		}
	};

	const getStatusBadge = (status: PosStatus) => {
		const statusConfig = {
			[PosStatus.ACTIVE]: {
				variant: "success",
				text: t("common.active"),
			},
			[PosStatus.INACTIVE]: {
				variant: "warning",
				text: t("common.inactive"),
			},
		};

		const config = statusConfig[status];
		return <Badge variant={getBestBadgeVariant(config.variant)}>{config.text}</Badge>;
	};

	if (loading) return <Loading />;

	if (!pos) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">{t("pos.errors.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("pos.detail.title")}</PageHeading>
						<p className="text-muted-foreground">
							{t("pos.detail.description")} - {pos.name}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-2">
					<PermissionGate permission="pos.update">
						<Button variant="outline" onClick={() => navigate(`/pos/${id}/edit`)}>
							<Edit className="h-4 w-4 mx-2" />
							{t("common.edit")}
						</Button>
					</PermissionGate>
					<PermissionGate permission="pos.delete">
						<Button variant="destructive" onClick={handleDelete}>
							<Trash2 className="h-4 w-4 mx-2" />
							{t("common.delete")}
						</Button>
					</PermissionGate>
				</div>
			</div>

			{/* POS Info Summary */}
			<Card>
				<CardHeader>
					<CardTitle className="flex flex-wrap items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							<Monitor className="h-5 w-5" />
							{t("pos.detail.posInfo")}
						</div>
						<div className="flex items-center gap-2">
							<Label>{t("pos.form.status")}</Label>
							<div>{getStatusBadge(pos.status)}</div>
						</div>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						<div className="space-y-2">
							<Label>{t("pos.form.name")}</Label>
							<Input aria-label={t("pos.form.name")} value={pos.name} readOnly />
						</div>
						<div className="space-y-2">
							<Label>{t("pos.table.posNumber")}</Label>
							<Input aria-label={t("pos.table.posNumber")} value={pos.id} readOnly />
						</div>
						{pos.menuSlug ? (
							<div className="space-y-2">
								<Label>{t("pos.form.menuSlug")}</Label>
								<div className="flex items-center gap-2">
									<Input
										aria-label={t("pos.form.menuSlug")}
										value={`https://store.cashiero.net/${pos.menuSlug}`}
										readOnly
									/>
									<Button
										variant="outline"
										onClick={() =>
											window.open(`https://store.cashiero.net/${pos.menuSlug}`, "_blank")
										}
									>
										<ExternalLink className="h-4 w-4 me-1" />
										{t("common.view")}
									</Button>
								</div>
							</div>
						) : null}
						<div className="space-y-2">
							<Label>{t("pos.form.code")}</Label>
							<Input aria-label={t("pos.form.code")} value={pos.code || "-"} readOnly />
						</div>
						<div className="space-y-2">
							<Label>{t("pos.form.serialNumber")}</Label>
							<Input aria-label={t("pos.form.serialNumber")} value={pos.serialNumber || "-"} readOnly />
						</div>
						<div className="space-y-2">
							<Label>{t("pos.table.createdAt")}</Label>
							<Input aria-label={t("pos.table.createdAt")} value={formatDate(pos.createdAt)} readOnly />
						</div>
					</div>
					<div className="space-y-2">
						<Label>{t("pos.form.notes")}</Label>
						<Input aria-label={t("pos.form.notes")} value={pos.notes || ""} readOnly />
					</div>
				</CardContent>
			</Card>

			{pos.isMenuWeb ? (
				<StorefrontBannersSettings
					banners={storefrontBanners}
					onChange={setStorefrontBanners}
					editing={false}
				/>
			) : null}

			{pos.isMenuWeb && <StorefrontSettingsEditor key={pos.id} posId={String(pos.id)} products={pos.products || []} banners={storefrontBanners} />}

			{/* Branch Details */}
			{pos.branch && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<Building2 className="h-5 w-5" />
							{t("pos.detail.branchInfo")}
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div className="space-y-2">
								<Label>{t("branches.fields.name")}</Label>
								<Input aria-label={t("branches.fields.name")} value={pos.branch.name} readOnly />
							</div>
							<div className="space-y-2">
								<Label>{t("branches.fields.code")}</Label>
								<Input aria-label={t("branches.fields.code")} value={pos.branch.code || "-"} readOnly />
							</div>
							{pos.branch.address && (
								<div className="space-y-2">
									<Label>{t("branches.fields.address")}</Label>
									<Input
										aria-label={t("branches.fields.address")}
										value={pos.branch.address}
										readOnly
									/>
								</div>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Tables (restaurant mode) */}
			{pos.offersTableService && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-base">
							<LayoutGrid className="h-5 w-5" />
							{t("tablesAdmin.title")} ({tables.length})
						</CardTitle>
					</CardHeader>
					<CardContent>
						{tables.length > 0 ? (
							<div className="record-surface">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>{t("tablesAdmin.table.number")}</TableHead>
											<TableHead>{t("tablesAdmin.table.label")}</TableHead>
											<TableHead>{t("tablesAdmin.table.floor")}</TableHead>
											<TableHead>{t("tablesAdmin.table.zone")}</TableHead>
											<TableHead className="text-center">
												{t("tablesAdmin.table.seats")}
											</TableHead>
											<TableHead className="text-center">
												{t("tablesAdmin.table.status")}
											</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{tables.map(tbl => (
											<TableRow key={tbl.id}>
												<TableCell className="font-semibold">{tbl.number}</TableCell>
												<TableCell>{tbl.label || "-"}</TableCell>
												<TableCell>{tbl.floor || "-"}</TableCell>
												<TableCell>{tbl.zone || "-"}</TableCell>
												<TableCell className="text-center">{tbl.seats ?? "-"}</TableCell>
												<TableCell className="text-center">
													<Badge variant={tbl.isActive ? "success" : "secondary"}>
														{tbl.isActive
															? t("tablesAdmin.active")
															: t("tablesAdmin.inactive")}
													</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						) : (
							<p className="text-muted-foreground text-center py-4">{t("tablesAdmin.empty")}</p>
						)}
					</CardContent>
				</Card>
			)}

			{/* Assigned Employees */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<Users className="h-5 w-5" />
						{t("pos.detail.assignedEmployees")} ({pos.employees?.length || 0})
					</CardTitle>
				</CardHeader>
				<CardContent>
					{pos.employees && pos.employees.length > 0 ? (
						<div className="record-surface">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("users.table.headers.employeeNumber")}</TableHead>
										<TableHead>{t("users.table.headers.name")}</TableHead>
										<TableHead>{t("users.table.headers.email")}</TableHead>
										<TableHead>{t("users.table.headers.phone")}</TableHead>
										<TableHead>{t("users.table.headers.role")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{pos.employees.map(employee => (
										<TableRow key={employee.id}>
											<TableCell>{employee.id || "-"}</TableCell>
											<TableCell>
												{employee.firstName} {employee.lastName}
											</TableCell>
											<TableCell>{employee.email}</TableCell>
											<TableCell className="rtl:text-right" dir="ltr">
												{employee.phone || "-"}
											</TableCell>
											<TableCell>{employee.role?.name || "-"}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					) : (
						<p className="text-muted-foreground text-center py-4">{t("pos.detail.noEmployees")}</p>
					)}
				</CardContent>
			</Card>

			{/* Assigned Products */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2 text-base">
						<Package className="h-5 w-5" />
						{t("pos.detail.assignedProducts")} ({pos.products?.length || 0})
					</CardTitle>
				</CardHeader>
				<CardContent>
					{pos.products && pos.products.length > 0 ? (
						<div className="record-surface">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t("products.table.name")}</TableHead>
										<TableHead>{t("products.table.price")}</TableHead>
										<TableHead>{t("products.table.stock")}</TableHead>
										<TableHead>{t("products.table.category")}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{pos.products.map(product => (
										<TableRow key={product.id}>
											<TableCell>
												<div className="font-medium">{product.name}</div>
												{product.barcode && (
													<div className="text-sm text-muted-foreground">
														{t("products.table.barcode")}: {product.barcode}
													</div>
												)}
											</TableCell>
											<TableCell>
												<Price value={Number(product.sellingPrice) || 0} currency={currency} />
											</TableCell>
											<TableCell>{product.quantity || 0}</TableCell>
											<TableCell>
												{product.category ? (
													<Badge variant="outline">{product.category.name}</Badge>
												) : (
													<span className="text-muted-foreground">-</span>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					) : (
						<p className="text-muted-foreground text-center py-4">{t("pos.detail.noProducts")}</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
};

export default PosViewPage;
